import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function KidsDecodeOutput() {
  // In production, this would come from API based on child's inputs
  const childName = 'Alex'; // First name only
  const decodeData = {
    coreTemperament: 'This child has an internally-focused processing style. Experiences are taken in, held internally, and processed before being expressed outward. This is a baseline orientation, not a choice or learned behavior.',
    worldExperience: 'Sensory input is absorbed gradually rather than immediately reacted to. New environments, people, or situations are observed and assessed before engagement. This processing pace is consistent across contexts and is not situational anxiety or shyness.',
    emotionalProcessing: 'Emotions are felt deeply but expressed selectively. Internal emotional experience is rich and sustained, but outward expression is measured. This does not indicate emotional suppression or difficulty—it reflects a natural processing rhythm.',
    regulationNeeds: 'This child requires lower stimulation environments to maintain regulation. High-energy or chaotic settings create processing overload rather than excitement. Transitions require internal adjustment time. Sensitivity to environmental intensity is neurological, not behavioral.',
    misinterpretations: [{
      mistaken: 'Withdrawn or antisocial',
      actual: 'Processing internally before engaging'
    }, {
      mistaken: 'Anxious or fearful',
      actual: 'Observing and assessing before participating'
    }, {
      mistaken: 'Emotionally distant',
      actual: 'Experiencing emotions internally with selective expression'
    }],
    boundaryStatement: 'This decode does not assess behavior quality, intelligence, emotional health, or parenting effectiveness. It describes temperament and processing style only.'
  };
  return <>
      <Helmet data-id="element-2214">
        <title data-id="element-2215">Kids Decode — Output | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-2216" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2217">
        {/* Navigation */}
        <header data-id="element-2218">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-2219">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2220">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-2221">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2222" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2223">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-2224">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2225">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-2226">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-2227">
          <div className="max-w-3xl mx-auto" data-id="element-2228">
            {/* Page Header */}
            <div className="mb-16" data-id="element-2229">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2230">
                Kids Decode
              </h1>
              <p className="text-sm text-[#F5F5F5]/50 mb-2" data-id="element-2231">
                Temperament and processing style overview
              </p>
              <p className="text-sm text-[#F5F5F5]/40" data-id="element-2232">
                Generated for: {childName}
              </p>
            </div>

            {/* Section 1: Core Temperament */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2233">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-2234">
                Core temperament
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2235">
                {decodeData.coreTemperament}
              </p>
            </section>

            {/* Section 2: How the Child Experiences the World */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2236">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-2237">
                How this child experiences the world
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2238">
                {decodeData.worldExperience}
              </p>
            </section>

            {/* Section 3: Emotional Processing Style */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2239">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-2240">
                Emotional processing style
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2241">
                {decodeData.emotionalProcessing}
              </p>
            </section>

            {/* Section 4: Regulation & Stimulation Needs */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2242">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-2243">
                Regulation and stimulation needs
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2244">
                {decodeData.regulationNeeds}
              </p>
            </section>

            {/* Section 5: Common Misinterpretations */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2245">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-2246">
                Common misinterpretations
              </h2>
              <div className="space-y-6" data-id="element-2247">
                {decodeData.misinterpretations.map((item, index) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2248">
                    <p className="text-[#F5F5F5]/50 text-sm mb-2" data-id="element-2249">
                      May be mistaken as:
                    </p>
                    <p className="text-[#F5F5F5]/70 mb-3" data-id="element-2250">{item.mistaken}</p>
                    <p className="text-[#F5F5F5]/50 text-sm mb-2" data-id="element-2251">
                      But is actually:
                    </p>
                    <p className="text-[#F5F5F5]/70" data-id="element-2252">{item.actual}</p>
                  </div>)}
              </div>
            </section>

            {/* Section 6: Boundary Statement */}
            <section className="mb-12" data-id="element-2253">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-2254">
                What this decode does not assess
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2255">
                {decodeData.boundaryStatement}
              </p>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-2256">
          <div className="max-w-7xl mx-auto text-center" data-id="element-2257">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-2258">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}